import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const printPdf = {
  /**
   * Generate accounts receivable report in PDF format
   * @param {Array} data - Array of accounts receivable data
   * @param {Object} options - Report options
   * @param {string} options.title - Report title
   * @param {string} options.startDate - Start date for the report
   * @param {string} options.endDate - End date for the report
   * @param {string} options.companyName - Company name
   * @param {string} options.companyAddress - Company address
   */
  /**
   * Check if we need a new page for the next content
   * @param {Object} doc - jsPDF document instance
   * @param {number} contentHeight - Height of the content to be added (in mm)
   * @param {number} currentY - Current Y position on the page
   * @returns {Object} Object containing newY and pageBreak flag
   */
  checkPageBreak(doc, contentHeight, currentY) {
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginBottom = 20; // Bottom margin in mm

    if (currentY + contentHeight > pageHeight - marginBottom) {
      doc.addPage();
      return { newY: 20, pageBreak: true };
    }
    return { newY: currentY, pageBreak: false };
  },

  generateAccountsReceivableReport(data, options = {}) {
    // Default options
    const defaultOptions = {
      title: 'LAPORAN PIUTANG',
      startDate: new Date().toLocaleDateString('id-ID'),
      endDate: new Date().toLocaleDateString('id-ID'),
      companyName: process.env.VUE_APP_STORE_NAME || 'Nama Perusahaan',
      companyAddress: process.env.VUE_APP_STORE_ADDRESS || 'Alamat Perusahaan',
      companyPhone: process.env.VUE_APP_STORE_PHONE || '0812-3456-7890',
      fontSizeTable: 8,
      ...options
    };

    // Create new PDF document
    const doc = new jsPDF({
      orientation: defaultOptions.orientation || 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Initial Y position
    let currentY = 20;

    // Add first page header
    currentY = this.addHeader(doc, defaultOptions, true);

    // Add the main table with proper options
    this.addAccountsReceivableTable(doc, {
      ...data,
      startDate: defaultOptions.startDate,
      endDate: defaultOptions.endDate
    }, {
      startY: currentY,
      fontSizeTable: defaultOptions.fontSizeTable
    });

    // Update currentY after table is drawn
    if (data?.summary) {
      currentY = doc.lastAutoTable.finalY + 10; // Reduced from 20 to 10 for better spacing

      // Add summary section with page break check
      const summaryHeight = (data.summary.length + 1) * 8 + 10; // Reduced from 50 to 40 for better spacing
      const summaryPageCheck = this.checkPageBreak(doc, summaryHeight, currentY);

      // Add summary
      doc.setFontSize(10);
      this.addSummary(doc, data, 14, summaryPageCheck.newY);
    }

    // Add summary table with page break check
    if (data?.summaryTableData) {
      currentY = doc.lastAutoTable.finalY + 10; // Reduced from 20 to 10 for better spacing
      const summaryTableData = data?.summaryTableData?.summaryTableData;

      const tableHeight = (summaryTableData.length + 1) * 8 + 10; // Calculate table height
      const tablePageCheck = this.checkPageBreak(doc, tableHeight, currentY);

      // Call with custom options
      currentY = this.addSummaryTable(doc, summaryTableData, 14, tablePageCheck.newY, {
        columnWidths: [40, 5, 30, 30, 30, 30], // Diperlebar dari [20, 30, 30, 30, 30]
        headers: data?.summaryTableData?.summaryTableHeader,
        fontSize: 9,
      });
    }

    // Add footer to all pages
    this.addFooter(doc, defaultOptions);

    // Save the PDF
    const fileName = `${defaultOptions.title}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  },

  /**
   * Add header to the PDF
   * @param {Object} doc - jsPDF document instance
   * @param {Object} options - Header options
   */
  addHeader(doc, options, isFirstPage = true) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const startY = isFirstPage ? 15 : 10; // Start higher on subsequent pages
    const centerX = pageWidth / 2;
    const rightMargin = pageWidth - 14; // 14 is the right margin

    // Company Name
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(options.companyName, centerX, startY, { align: 'center' });

    // Company Address
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(options.companyAddress, centerX, startY + 5, { align: 'center' });

    // Company Phone
    doc.setFontSize(10);
    doc.text(options.companyPhone, centerX, startY + 10, { align: 'center' });

    // Line separator - full width
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.line(14, startY + 12, rightMargin, startY + 12);

    // Title
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(options.title, centerX, startY + 17, { align: 'center' });

    // Date Range - aligned to left
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Periode: ${options.startDate} s/d ${options.endDate}`, 14, startY + 25);

    if (!isFirstPage) {
      // Add page number for subsequent pages - aligned to right
      const pageCount = doc.internal.getNumberOfPages();
      doc.text(`Halaman ${pageCount}`, rightMargin - 5, startY + 5, { align: 'right' });
    }

    // Bottom line separator - full width
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.line(14, startY + 30, rightMargin, startY + 30);

    return startY + 35; // Return Y position after header
  },

  /**
   * Add accounts receivable table to the PDF
   * @param {Object} doc - jsPDF document instance
   * @param {Array} data - Accounts receivable data
   * @param {number} startY - Y position to start the table
   */
  addAccountsReceivableTable(doc, data, options = {}) {
    try {
      // Default table columns
      let tableColumn = [
        { header: 'No', dataKey: 'no' },
        { header: 'Kode', dataKey: 'code' },
        { header: 'Nama Pelanggan', dataKey: 'customer_name' },
        { header: 'Tgl Transaksi', dataKey: 'transaction_date' },
        { header: 'Jatuh Tempo', dataKey: 'due_date' },
        { header: 'Total', dataKey: 'grand_total' },
        { header: 'Terbayar', dataKey: 'paid_amount' },
        { header: 'Sisa', dataKey: 'remaining' },
        { header: 'Status', dataKey: 'status' }
      ];

      // Default column styles
      let columnStyle = {
        0: { cellWidth: 10 }, // No
        1: { cellWidth: 25 }, // Kode
        2: { cellWidth: 30 }, // Nama Pelanggan
        3: { cellWidth: 25 }, // Tgl Transaksi
        4: { cellWidth: 25 }, // Jatuh Tempo
        5: { cellWidth: 25, halign: 'right' }, // Total
        6: { cellWidth: 25, halign: 'right' }, // Terbayar
        7: { cellWidth: 25, halign: 'right' }, // Sisa
        8: { cellWidth: 20, halign: 'center' } // Status
      };

      // Use custom headers if provided
      if (data.tableHeader && Array.isArray(data.tableHeader)) {
        tableColumn = data.tableHeader;
      }

      // Use custom column styles if provided
      if (data.tableColumnStyle && typeof data.tableColumnStyle === 'object') {
        columnStyle = data.tableColumnStyle;
      }

      // Get the table data - check both reportData and direct array
      const tableData = data.reportData || (Array.isArray(data) ? data : []);

      if (!tableData || tableData.length === 0) {
        console.warn('No data available for the table');
        return null;
      }

      // Format the data for the table
      const tableRows = tableData.map((row) => {
        return tableColumn.map(col => {
          // Return the value or empty string if undefined
          return row[col.dataKey] !== undefined ? String(row[col.dataKey]) : '';
        });
      });

      // Create the table configuration
      const tableConfig = {
        head: [tableColumn.map(col => col.header)],
        body: tableRows,
        startY: options.startY,
        margin: {
          top: 10,
          left: 14,
          right: 14
        },
        styles: {
          overflow: 'linebreak',
          fontSize: options.fontSizeTable || 8,
          // cellPadding: 3,
          lineColor: [200, 200, 200],
          lineWidth: 0.1
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: options.fontSizeTable || 8,
          // cellPadding: 4,
          halign: 'center'
        },
        bodyStyles: {
          fontSize: options.fontSizeTable || 8,
          // cellPadding: 3,
          lineColor: [200, 200, 200],
          lineWidth: 0.1
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        columnStyles: columnStyle,
        didDrawPage: () => {
          // Add header to each new page
          // if (data.pageNumber > 1) {
          //   doc.setFontSize(10);
          //   doc.setFont('helvetica', 'normal');
          //   doc.text(`Halaman ${data.pageNumber}`, 190, 15, { align: 'right' });
          //   doc.text(`Periode: ${options.startDate} s/d ${options.endDate}`, 14, 15);
          // }
        },
        didParseCell: () => {
          // Add any cell parsing logic here if needed
        },
        willDrawCell: () => {
          // Add any custom cell drawing logic here if needed
        },
        fillColor: [245, 245, 245],
        theme: 'grid',
        tableWidth: 'wrap'
      };

      // Generate the table
      doc.autoTable(tableConfig);

      return tableConfig;
    } catch (error) {
      console.error('Error in addAccountsReceivableTable:', error);
      return null;
    }
  },

  /**
   * Add summary section to the PDF
   * @param {Object} doc - jsPDF document instance
   * @param {Array} data - Accounts receivable data
   * @param {number} x - X position
   * @param {number} y - Y position
   */
  addSummary(doc, data, x, y) {
    try {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('RINGKASAN', x, y);

      doc.setFont('helvetica', 'normal');

      // Ensure summary is an array before trying to iterate
      const summary = Array.isArray(data?.summary) ? data.summary : [];
      console.log("Summary data:", summary);

      // Draw each summary item
      summary.forEach((item, index) => {
        if (item && item.label) {
          const yPos = y + (index + 1) * 7;
          doc.text(String(item.label), x, yPos);

          // Format the value if it exists
          const value = item.value !== undefined ? String(item.value) : '';
          const rightEdge = x + 100; // Right edge of the text box
          doc.text(value, rightEdge, yPos, { align: 'right' });
        }
      });

      // Add any additional summary information here
      doc.setFont('helvetica', 'bold');
      // Example of how to add additional summary lines:
      // const totalY = y + (summary.length + 1) * 7;
      // doc.text('Total:', x, totalY);
      // doc.text(this.formatCurrency(data.total || 0), rightEdge, totalY, { align: 'right' });
    } catch (error) {
      console.error('Error in addSummary:', error);
      // Optionally rethrow the error if you want it to be handled by the caller
      // throw error;
    }
  },

  /**
   * Add summary table to the PDF
   * @param {Object} doc - jsPDF document instance
   * @param {Array} data - Summary data array
   * @param {number} x - X position
   * @param {number} y - Y position
   */
  addSummaryTable(doc, data, x, y, options = {}) {
    try {
      const startX = x;
      const startY = y;
      const rowHeight = options.rowHeight || 12;
      const fontSize = options.fontSize || 10;
      const textColor = options.textColor || [0, 0, 0];
      const boldLastRow = options.boldLastRow !== false;
      const showHeader = options.showHeader !== false;

      // Default column widths if not provided
      const defaultColumnWidths = [100, 80, 60, 60]; // Default widths for up to 4 columns
      const columnWidths = options.columnWidths || defaultColumnWidths;

      // Calculate total table width
      const tableWidth = columnWidths.reduce((sum, width) => sum + width, 0);

      // Default padding
      const padding = {
        left: options.padding?.left || 0,
        right: options.padding?.right || 0,
        top: options.padding?.top || 0,
        bottom: options.padding?.bottom || 0
      };

      // Border style options
      const borderStyle = {
        // Outer borders
        top: options.border?.top ?? false,
        right: options.border?.right ?? false,
        bottom: options.border?.bottom ?? false,
        left: options.border?.left ?? false,
        // Inner borders
        innerV: options.border?.innerV ?? false, // vertical inner borders
        innerH: options.border?.innerH ?? false  // horizontal inner borders
      };

      // Cell borders - can be set per cell
      const cellBorders = options.cellBorders || {};

      // Validate input data
      if (!Array.isArray(data) || data.length === 0) {
        console.warn('No data provided for summary table');
        return y;
      }

      // Set initial font
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fontSize);
      doc.setTextColor(...textColor);

      // Draw header if enabled
      if (showHeader && options.headers) {
        doc.setFillColor(255, 255, 255);
        doc.rect(startX, startY, tableWidth, rowHeight, 'F');

        // Draw header borders
        if (borderStyle.top) {
          doc.setDrawColor(0, 0, 0);
          doc.line(startX, startY, startX + tableWidth, startY);
        }

        // Draw header cells
        options.headers.forEach((header, colIndex) => {
          // Use the same calculation as in the body cells
          const xPos = startX + columnWidths.slice(0, colIndex).reduce((sum, w) => sum + w, 0);
          const width = columnWidths[colIndex];
          const align = colIndex === 0 ? 'left' : 'right';

          // Draw cell background
          doc.setFillColor(255, 255, 255);
          doc.rect(xPos, startY, width, rowHeight, 'F');

          // Draw cell text
          doc.setFont('helvetica', 'bold');
          doc.text(
            header,
            xPos + (align === 'left' ? padding.left : width - padding.right),
            startY + rowHeight / 2 + 3,
            {
              align,
              baseline: 'middle',
              maxWidth: width - (padding.left + padding.right) // Prevent text overflow
            }
          );

          // Draw right border if not the last column
          // if (colIndex < options.headers.length - 1) {
          //   doc.setDrawColor(200, 200, 200);
          //   doc.line(xPos + width, startY, xPos + width, startY + rowHeight);
          // }

          // Add bottom border to all header cells
          doc.setDrawColor(0, 0, 0); // Black color for bottom border
          doc.line(xPos, startY + rowHeight, xPos + width, startY + rowHeight);
        });
      }

      // Draw rows
      data.forEach((row, rowIndex) => {
        const currentY = startY + (showHeader ? (rowIndex + 1) * rowHeight : rowIndex * rowHeight);
        const isLastRow = rowIndex === data.length - 1;

        // Set font weight for last row if boldLastRow is true
        if (isLastRow && boldLastRow) {
          doc.setFont('helvetica', 'bold');
        }

        // Draw row background for even rows
        // if (rowIndex % 2 === 0) {
        //   doc.setFillColor(250, 250, 250);
        //   doc.rect(startX, currentY, tableWidth, rowHeight, 'F');
        // }

        // Draw cells
        row.forEach((cell, colIndex) => {
          // Calculate x position by summing up all previous column widths
          const xPos = startX + columnWidths.slice(0, colIndex).reduce((sum, w) => sum + w, 0);
          const width = columnWidths[colIndex];
          const align = colIndex === 0 ? 'left' : 'right';
          const cellPadding = cell.padding || padding;

          // Apply cell-specific styles
          const cellStyle = {
            bgColor: cell.bgColor || [255, 255, 255], // Removed zebra striping for now
            textColor: cell.textColor || textColor,
            align: cell.align || align,
            bold: cell.bold || false
          };

          // Draw cell background
          doc.setFillColor(...cellStyle.bgColor);
          doc.rect(xPos, currentY, width, rowHeight, 'F');

          // Draw cell text
          doc.setTextColor(...cellStyle.textColor);
          doc.setFont('helvetica', cellStyle.bold ? 'bold' : 'normal');

          let displayValue = '';
          if (cell.value !== undefined) {
            if (cell.type === 'currency') {
              displayValue = this.formatCurrency(cell.value);
            } else if (cell.type === 'number') {
              displayValue = cell.value.toLocaleString('id-ID');
            } else {
              displayValue = String(cell.value);
            }
          } else if (cell.text !== undefined) {
            displayValue = String(cell.text);
          }

          // Adjust text position to prevent overflow
          const textX = cellStyle.align === 'left'
            ? xPos + cellPadding.left
            : xPos + width - cellPadding.right;

          doc.text(
            displayValue,
            textX,
            currentY + rowHeight / 2 + 3,
            {
              align: cellStyle.align,
              baseline: 'middle',
              maxWidth: width - (cellPadding.left + cellPadding.right) // Prevent text overflow
            }
          );

          // Draw cell borders
          const cellBorder = cell.border || cellBorders[`${rowIndex},${colIndex}`];
          if (cellBorder) {
            doc.setDrawColor(...(cellBorder.color || [0, 0, 0]));
            if (cellBorder.top) doc.line(xPos, currentY, xPos + width, currentY);
            if (cellBorder.right) doc.line(xPos + width, currentY, xPos + width, currentY + rowHeight);
            if (cellBorder.bottom) doc.line(xPos, currentY + rowHeight, xPos + width, currentY + rowHeight);
            if (cellBorder.left) doc.line(xPos, currentY, xPos, currentY + rowHeight);
          }
        });

        // Draw horizontal inner borders
        if (borderStyle.innerH && !isLastRow) {
          doc.setDrawColor(200, 200, 200);
          doc.line(startX, currentY + rowHeight, startX + tableWidth, currentY + rowHeight);
        }

        // Reset font weight if we changed it
        if (isLastRow && boldLastRow) {
          doc.setFont('helvetica', 'normal');
        }
      });

      // Draw outer borders
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);

      const totalHeight = (data.length + (showHeader ? 1 : 0)) * rowHeight;

      if (borderStyle.top) {
        doc.line(startX, startY, startX + tableWidth, startY);
      }
      if (borderStyle.right) {
        doc.line(startX + tableWidth, startY, startX + tableWidth, startY + totalHeight);
      }
      if (borderStyle.bottom) {
        doc.line(startX, startY + totalHeight, startX + tableWidth, startY + totalHeight);
      }
      if (borderStyle.left) {
        doc.line(startX, startY, startX, startY + totalHeight);
      }

      // Return the Y position after the table with some padding
      return startY + totalHeight + (options.padding?.bottom || 10);
    } catch (error) {
      console.error('Error in addSummaryTable:', error);
      return y;
    }
  },

  /**
   * Add footer to the PDF
   * @param {Object} doc - jsPDF document instance
   * @param {Object} options - Footer options
   */
  addFooter(doc) {
    const pageCount = doc.internal.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
      // Set current page
      doc.setPage(i);

      // Page number
      doc.setFontSize(8);
      doc.text(
        `Halaman ${i} dari ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );

      // Generated info
      doc.text(
        `Dicetak pada: ${new Date().toLocaleString('id-ID')}`,
        doc.internal.pageSize.getWidth() - 14,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'right' }
      );
    }
  },

  /**
   * Format number to currency
   * @param {number} amount - Amount to format
   * @returns {string} Formatted currency string
   */
  formatCurrency(amount) {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }
};

export default printPdf;